// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { Dropdown, Tag, Tooltip, Typography, type MenuProps } from "antd";
import { MoreOutlined, SafetyCertificateTwoTone } from "@ant-design/icons";
import { Power, roleName, roleSigil, type PowerLevels } from "../types";

// MemberList is the right rail nick list, IRC-style: members ordered by role
// with @ / + / ~ sigils, an E2E badge, and a right-click/click context menu for
// operator actions (promote, voice, kick, ban) gated by the viewer's own power.
export function MemberList({
  members,
  power,
  usersDefault,
  names,
  statuses,
  presence,
  rateLimits,
  keyed,
  myUserID,
  myPower,
  isDm = false,
  onMessage,
  onSetPower,
  onSetRate,
  onKick,
  onBan,
  chanServRegistered,
  onChanServAccess,
}: {
  readonly members: string[];
  readonly power: PowerLevels;
  readonly usersDefault: number;
  readonly names: Record<string, string>;
  readonly statuses: Record<string, string>;
  readonly presence: Record<string, boolean>;
  readonly rateLimits: Record<string, number>;
  readonly keyed: Set<string>;
  readonly myUserID: string;
  readonly myPower: number;
  // isDm hides per-member role actions (op/voice/kick/ban) — meaningless in a 1:1.
  readonly isDm?: boolean;
  // onMessage opens a 1:1 DM with the given user (any member, not just ops).
  readonly onMessage: (userID: string) => void;
  readonly onSetPower: (userID: string, level: number) => void;
  readonly onSetRate: (userID: string, seconds: number) => void;
  readonly onKick: (userID: string) => void;
  readonly onBan: (userID: string) => void;
  readonly chanServRegistered?: boolean;
  readonly onChanServAccess?: (userID: string, role: "op" | "voice" | "none") => void;
}) {
  const levelOf = (uid: string) => power[uid] ?? usersDefault;
  const nameOf = (uid: string) => names[uid] || uid.replace(/^@/, "").split(":")[0];

  const sorted = [...members].sort((a, b) => {
    const d = levelOf(b) - levelOf(a);
    return d !== 0 ? d : nameOf(a).localeCompare(nameOf(b));
  });

  // menuFor builds the actions against `uid`: everyone can Message any other user;
  // operators additionally get moderation actions when they outrank the target.
  function menuFor(uid: string): MenuProps["items"] | null {
    // In a 1:1 DM there are no roles to manage and "Message" is redundant — no menu.
    if (isDm || uid === myUserID) return null;
    const items: MenuProps["items"] = [{ key: "message", label: "Message", onClick: () => onMessage(uid) }];

    const lvl = levelOf(uid);
    if (myPower < Power.Op || lvl >= myPower) return items; // not a moderator over them

    const rateOptions = [0, 5, 15, 30, 60].map((s) => ({
      key: `rate-${s}`,
      label: s === 0 ? "Off" : `${s}s`,
      onClick: () => onSetRate(uid, s),
    }));
    items.push({ type: "divider" });
    // Only a Founder can mint another Founder — i.e. hand over ownership.
    if (myPower >= Power.Founder && lvl < Power.Founder) {
      items.push({ key: "founder", label: "Make Founder (transfer ownership)", onClick: () => onSetPower(uid, Power.Founder) });
    }
    if (lvl < Power.Op) items.push({ key: "op", label: "Make Operator", onClick: () => onSetPower(uid, Power.Op) });
    if (lvl >= Power.Op) items.push({ key: "deop", label: "Remove Operator", onClick: () => onSetPower(uid, Power.Voice) });
    if (lvl < Power.Voice) items.push({ key: "voice", label: "Give Voice", onClick: () => onSetPower(uid, Power.Voice) });
    if (lvl >= Power.Voice && lvl < Power.Op) items.push({ key: "devoice", label: "Remove Voice", onClick: () => onSetPower(uid, Power.Regular) });
    items.push({ key: "rate", label: rateLimits[uid] ? `Rate limit (${rateLimits[uid]}s)` : "Rate limit", children: rateOptions });
    // ChanServ persistent access (survives the user leaving/rejoining).
    if (chanServRegistered && onChanServAccess) {
      items.push({
        key: "chanserv",
        label: "ChanServ access",
        children: [
          { key: "cs-op", label: "Auto-op", onClick: () => onChanServAccess(uid, "op") },
          { key: "cs-voice", label: "Auto-voice", onClick: () => onChanServAccess(uid, "voice") },
          { key: "cs-none", label: "Clear access", onClick: () => onChanServAccess(uid, "none") },
        ],
      });
    }
    items.push({ type: "divider" });
    items.push({ key: "kick", danger: true, label: "Kick", onClick: () => onKick(uid) });
    items.push({ key: "ban", danger: true, label: "Ban", onClick: () => onBan(uid) });
    return items;
  }

  return (
    <div className="rail">
      <div className="rail-head">
        <Typography.Text strong>Members</Typography.Text>
        <Tag>{members.length}</Tag>
      </div>
      <div className="rail-list">
        {sorted.length === 0 && <div className="rail-empty">no members</div>}
        {sorted.map((uid) => {
          const lvl = levelOf(uid);
          const sigil = roleSigil(lvl);
          const items = menuFor(uid);
          const away = statuses[uid];
          const online = presence[uid];
          const row = (
            <div
              className={`member-item ${items ? "has-actions" : ""} ${away ? "member-away" : ""}`}
              onDoubleClick={() => !isDm && uid !== myUserID && onMessage(uid)}
              title={isDm || uid === myUserID ? "" : "Double-click to message"}
            >
              <Tooltip title={online ? "Online" : "Offline"}>
                <span className={`presence-dot ${online ? "online" : "offline"}`} />
              </Tooltip>
              <Tooltip title={roleName(lvl)}>
                <span className={`sigil sigil-${sigil || "none"}`}>{sigil || " "}</span>
              </Tooltip>
              <Tooltip title={away ? `away: ${away}` : ""}>
                <span className="member-nick">{nameOf(uid)}</span>
              </Tooltip>
              {keyed.has(uid) && (
                <Tooltip title="End-to-end key published">
                  <SafetyCertificateTwoTone twoToneColor="#19c37d" />
                </Tooltip>
              )}
              {items && <MoreOutlined className="member-actions" title="Actions" />}
            </div>
          );
          return items ? (
            <Dropdown key={uid} trigger={["click", "contextMenu"]} menu={{ items }}>
              {row}
            </Dropdown>
          ) : (
            <div key={uid}>{row}</div>
          );
        })}
      </div>
    </div>
  );
}
